/**
 * SelectSlotStep Component
 * Slot selection UI. Defaults to a Calendly-style month calendar (pick a day,
 * then a time); a list view of all upcoming times is available as a toggle.
 */

import React, { useCallback, useMemo, useState } from 'react';
import { Calendar, CalendarDays, Clock, List, Utensils } from 'lucide-react';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Button } from '../../button.js';
import { parseDateString } from '../../../lib/date-utils.js';
import { AvailableDay, TimeSlot, BookingData } from '../utils/bookingConstants.js';
import { formatTimeZoneAbbreviation } from '../utils/bookingTimezones.js';
import { CalendarConnectionError } from '../CalendarConnectionError.js';
import { SlotCalendarView } from './SlotCalendarView.js';

type SlotViewMode = 'calendar' | 'list';

interface SelectSlotStepProps {
  bookingData: BookingData;
  isLoading: boolean;
  error: any;
  availableDays: AvailableDay[] | undefined;
  visibleMonth: Date;
  minMonth: Date;
  maxMonth: Date;
  onVisibleMonthChange: (month: Date) => void;
  onSlotSelect: (slot: TimeSlot) => void;
  onRetry: () => void;
}

export function SelectSlotStep({
  bookingData,
  isLoading,
  error,
  availableDays,
  visibleMonth,
  minMonth,
  maxMonth,
  onVisibleMonthChange,
  onSlotSelect,
  onRetry,
}: SelectSlotStepProps) {
  const [viewMode, setViewMode] = useState<SlotViewMode>('calendar');
  const timeZone = bookingData.timeZone;
  const zoneAbbreviation = formatTimeZoneAbbreviation(timeZone);

  const handleSlotClick = useCallback(
    (slot: TimeSlot) => {
      onSlotSelect(slot);
    },
    [onSlotSelect]
  );

  const hasDays = !!availableDays && availableDays.some((day) => day.slots.length > 0);

  // Merged multi-calendar availability arrives unsorted within a day
  const sortedDays = useMemo(
    () =>
      (availableDays ?? [])
        .filter((day) => day.slots.length > 0)
        .map((day) => ({
          ...day,
          slots: [...day.slots].sort((a, b) => a.start.localeCompare(b.start)),
        })),
    [availableDays]
  );

  return (
    <div className="space-y-6">
      {/* View toggle — always visible so the choice persists across month loads */}
      <div className="flex justify-end">
        <div
          role="group"
          aria-label="Time slot view"
          className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50"
        >
          <button
            type="button"
            onClick={() => setViewMode('calendar')}
            aria-pressed={viewMode === 'calendar'}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'calendar'
                ? 'bg-card text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <CalendarDays className="w-4 h-4" />
            Calendar
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-card text-blue-700 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <List className="w-4 h-4" />
            List
          </button>
        </div>
      </div>

      {isLoading && !hasDays ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading available times...</p>
        </div>
      ) : error ? (
        (error as any)?.code === 'CALENDAR_NOT_CONNECTED' || (error as any)?.statusCode === 422 ? (
          <CalendarConnectionError
            email={bookingData.bookeeEmail}
            errorMessage={error.message}
            onRetry={onRetry}
          />
        ) : (
          <div className="text-center py-12">
            <div className="text-red-600 mb-4">
              <Calendar className="w-12 h-12 mx-auto mb-2" />
              <p>Unable to load availability</p>
              <p className="text-sm text-gray-500 mt-2">{error.message || 'Please try again'}</p>
            </div>
            <div className="space-y-2">
              <Button onClick={onRetry} variant="outline" className="w-full sm:w-auto">
                Try Again
              </Button>
              <div className="text-xs text-gray-500">Booking with: {bookingData.bookeeEmail}</div>
            </div>
          </div>
        )
      ) : viewMode === 'calendar' ? (
        <SlotCalendarView
          bookingData={bookingData}
          availableDays={availableDays ?? []}
          visibleMonth={visibleMonth}
          minMonth={minMonth}
          maxMonth={maxMonth}
          isLoading={isLoading}
          onVisibleMonthChange={onVisibleMonthChange}
          onSlotSelect={handleSlotClick}
        />
      ) : !hasDays ? (
        <div className="text-center py-12">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-card-foreground mb-2">No Available Times</h3>
          <p className="text-gray-600 mb-4">No available slots found for this month.</p>
          <Button onClick={onRetry} variant="outline">
            Refresh Availability
          </Button>
        </div>
      ) : (
        <div className={`space-y-6 ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}>
          {sortedDays.map((day) => (
              <div key={day.date} className="space-y-3">
                <div className="flex items-baseline justify-between border-b border-gray-200 pb-2">
                  <h4 className="text-md font-semibold text-card-foreground">
                    {parseDateString(day.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </h4>
                  <p className="text-xs text-gray-500">
                    {day.slots.length} time{day.slots.length !== 1 ? 's' : ''} ·{' '}
                    {zoneAbbreviation}
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {day.slots.map((slot: TimeSlot, index) => {
                    const isSelected = bookingData.selectedSlot?.start === slot.start;
                    return (
                      <button
                        key={`${day.date}-${index}`}
                        type="button"
                        onClick={() => handleSlotClick(slot)}
                        className={`flex items-center justify-center gap-1.5 rounded-lg border py-2.5 px-2 text-sm font-semibold transition-colors ${
                          isSelected
                            ? slot.isLunchSlot
                              ? 'bg-orange-500 border-orange-500 text-white'
                              : 'bg-blue-600 border-blue-600 text-white'
                            : slot.isLunchSlot
                              ? 'border-orange-300 text-orange-700 hover:bg-orange-50'
                              : 'border-blue-200 text-blue-700 hover:border-blue-500 hover:bg-blue-50'
                        }`}
                      >
                        {slot.isLunchSlot ? (
                          <Utensils className="w-3.5 h-3.5 shrink-0" />
                        ) : (
                          <Clock className="w-3.5 h-3.5 shrink-0" />
                        )}
                        {formatInTimeZone(parseISO(slot.start), timeZone, 'h:mm a')}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
